import React, {useEffect, useState} from 'react'
import {Button, Input, Popconfirm, Table, Tooltip, message} from 'antd'
import {ArrowRightLeft, Zap} from 'lucide-react'
import {useTranslation} from 'react-i18next'
import {APIv1} from '../../api'
import {getAuthHeaders} from './version.config'
import ChangeVersionModal from './ChangeVersionModal'
import {playFeedbackWave} from "../../utils/feedbackWave";

const MAX_UPDATE_IDS = 1000

const getErrorText = error => {
    const data = error?.response?.data
    if (!data) return null
    if (typeof data.detail === 'string') return data.detail

    const first = Object.values(data).flat()[0]
    return typeof first === 'string' ? first : null
}

const VersionRelatedList = ({
    index,
    title,
    endpoint,
    columns,
    token,
    mapItem,
    searchPlaceholder,
    canEdit,
    updateEndpoint,
    versionsEndpoint,
    currentVersionId,
    confirmKey,
    fastConfirmKey,
    defaultPageSize = 40,
}) => {
    const {t} = useTranslation()
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(defaultPageSize)
    const [search, setSearch] = useState('')
    const [reloadKey, setReloadKey] = useState(0)
    const [selectedIds, setSelectedIds] = useState([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const isSelectable = Boolean(canEdit && updateEndpoint)

    useEffect(() => {
        if (!token || !endpoint) return

        const fetchItems = async () => {
            setLoading(true)
            try {
                const response = await APIv1.get(endpoint, {
                    params: {
                        page: currentPage,
                        page_size: pageSize,
                        search: search || undefined,
                    },
                    headers: getAuthHeaders(token),
                })
                const results = Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []

                setItems(results.map(mapItem))
                setTotal(response.data?.count ?? results.length)
            } catch (error) {
                console.error('Something went wrong:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchItems()
    }, [token, endpoint, currentPage, pageSize, search, mapItem, reloadKey])

    // Trigger only when Enter/search button is pressed or the input is cleared
    const handleSearch = value => {
        setSearch(value.trim())
        setCurrentPage(1)
    }

    const isOverLimit = () => {
        if (selectedIds.length <= MAX_UPDATE_IDS) return false

        message.warning(t('pages.versions.detail.related.change.limit', {max: MAX_UPDATE_IDS}))
        return true
    }

    const openModal = () => {
        if (isOverLimit()) return
        setIsModalOpen(true)
    }

    // either a specific version or fast_change (latest STABLE), never both
    const submitUpdate = async ({version, fastChange = false}) => {
        if (!fastChange && !version) return

        const payload = fastChange
            ? {fast_change: true, ids: selectedIds}
            : {version: version.id, ids: selectedIds}

        setSubmitting(true)
        try {
            const response = await APIv1.post(updateEndpoint, payload, {
                headers: getAuthHeaders(token),
            })
            playFeedbackWave('success');
            message.success(t('pages.versions.detail.related.change.success', {
                version: response.data?.version_number ?? version?.version_number,
                updated: response.data?.updated_count ?? 0,
                unchanged: response.data?.unchanged_count ?? 0,
            }))
            setIsModalOpen(false)
            setSelectedIds([])
            setReloadKey(key => key + 1)
        } catch (error) {
            console.error('Something went wrong:', error)
            playFeedbackWave('failure');
            message.error(getErrorText(error) || t('pages.versions.detail.related.change.error'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <section className='create-device__forms-container version-detail__related-section'>
            <div className={`create-device__section-header version-detail__related-header${selectedIds.length ? ' version-detail__related-header--selecting' : ''}`}>
                <span className='create-device__section-index'>{index}</span>
                <h2 className='create-device__forms-title' title={title}>
                    <span className='version-detail__related-title'>{title}</span>
                    <span className='version-detail__related-count'> ({total})</span>
                </h2>
                <Input.Search
                    className='version-detail__related-search'
                    placeholder={searchPlaceholder}
                    allowClear
                    enterButton
                    onSearch={handleSearch}
                />
                {isSelectable && (
                    // collapsed until rows are selected; its width animates so the search slides aside
                    <span
                        className={`version-detail__change-action${selectedIds.length ? ' version-detail__change-action--visible' : ''}`}
                        aria-hidden={!selectedIds.length}
                    >
                        <Tooltip title={t('pages.versions.detail.related.change.button', {count: selectedIds.length})}>
                            <Button
                                type='primary'
                                icon={<ArrowRightLeft size={14}/>}
                                tabIndex={selectedIds.length ? 0 : -1}
                                onClick={openModal}
                            >
                                {/* full label on wide cards, count only on narrow ones (see VersionDetail.scss) */}
                                <span className='version-detail__change-label'>
                                    {t('pages.versions.detail.related.change.button', {count: selectedIds.length})}
                                </span>
                                <span className='version-detail__change-count'>{selectedIds.length}</span>
                            </Button>
                        </Tooltip>
                        <Popconfirm
                            title={t('pages.versions.detail.related.change.fastConfirmTitle')}
                            description={t(fastConfirmKey, {count: selectedIds.length})}
                            okText={t('pages.versions.detail.related.change.submit')}
                            cancelText={t('pages.versions.detail.related.change.cancel')}
                            onConfirm={() => !isOverLimit() && submitUpdate({fastChange: true})}
                        >
                            <Tooltip title={t('pages.versions.detail.related.change.fastTooltip')}>
                                <Button
                                    color='primary'
                                    variant='filled'
                                    className='version-detail__fast-change'
                                    tabIndex={selectedIds.length ? 0 : -1}
                                    loading={submitting && !isModalOpen}
                                    aria-label={t('pages.versions.detail.related.change.fastTooltip')}
                                >
                                    <ArrowRightLeft size={14}/>
                                    <Zap size={14}/>
                                </Button>
                            </Tooltip>
                        </Popconfirm>
                    </span>
                )}
            </div>
            <Table
                columns={columns}
                dataSource={items}
                loading={loading}
                scroll={{x: 'max-content'}}
                rowSelection={isSelectable ? {
                    selectedRowKeys: selectedIds,
                    onChange: setSelectedIds,
                    preserveSelectedRowKeys: true,
                } : undefined}
                pagination={{
                    total,
                    current: currentPage,
                    pageSize,
                    hideOnSinglePage: true,
                    onChange: (page, size) => {
                        setCurrentPage(page)
                        setPageSize(size)
                    },
                }}
            />
            {isSelectable && (
                <ChangeVersionModal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSubmit={version => submitUpdate({version})}
                    submitting={submitting}
                    count={selectedIds.length}
                    confirmKey={confirmKey}
                    token={token}
                    versionsEndpoint={versionsEndpoint}
                    currentVersionId={currentVersionId}
                />
            )}
        </section>
    )
}

export default VersionRelatedList
